/*
  STS liquid glass
  Inspired by shuding/liquid-glass (MIT, Copyright 2025 Shu Ding).
  This theme version attaches subtle SVG displacement filters to real UI surfaces.
*/
(function () {
  'use strict';

  if (!('CSS' in window) || !CSS.supports('backdrop-filter', 'blur(1px)')) return;

  const targets = [
    { selector: '#header-component .header__columns', radius: 0, strength: 38, sampleWidth: 720 },
    { selector: '.sts-chart-card', radius: 20, strength: 46, sampleWidth: 520 },
  ];

  const svgNS = 'http://www.w3.org/2000/svg';
  const instances = new WeakMap();

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function smoothStep(edge0, edge1, value) {
    const t = clamp((value - edge0) / (edge1 - edge0), 0, 1);
    return t * t * (3 - 2 * t);
  }

  function roundedRectSDF(x, y, width, height, radius) {
    const qx = Math.abs(x) - width + radius;
    const qy = Math.abs(y) - height + radius;
    return Math.min(Math.max(qx, qy), 0) + Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) - radius;
  }

  function ensureHost() {
    let host = document.getElementById('sts-liquid-glass-filters');
    if (host) return host;

    host = document.createElementNS(svgNS, 'svg');
    host.setAttribute('id', 'sts-liquid-glass-filters');
    host.setAttribute('aria-hidden', 'true');
    host.setAttribute('focusable', 'false');
    host.classList.add('sts-liquid-glass-filter-host');
    document.body.appendChild(host);
    return host;
  }

  function buildMap(width, height, radius, strength) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    const image = context.createImageData(width, height);
    const data = image.data;
    const edgeBand = Math.max(18, Math.min(width, height) * 0.32);
    const centerX = width / 2;
    const centerY = height / 2;
    const lensRadius = Math.min(radius, width * 0.5, height * 0.5);
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const rawValues = [];
    let maxDisplacement = 1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const localX = x - centerX;
        const localY = y - centerY;
        const distanceToEdge = roundedRectSDF(localX, localY, halfWidth, halfHeight, lensRadius);
        const insideDistance = Math.max(0, -distanceToEdge);
        const edgeLens = 1 - smoothStep(0, edgeBand, insideDistance);
        const rimLens = Math.pow(clamp(edgeLens, 0, 1), 1.7);
        const lens = clamp(edgeLens * 0.36 + rimLens * 0.92, 0, 1);
        const normalizedX = localX / halfWidth;
        const normalizedY = localY / halfHeight;
        const pullX = normalizedX * strength * lens;
        const pullY = normalizedY * strength * lens;

        rawValues.push(pullX, pullY);
        maxDisplacement = Math.max(maxDisplacement, Math.abs(pullX), Math.abs(pullY));
      }
    }

    const scale = maxDisplacement * 0.5;
    let valueIndex = 0;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const index = (y * width + x) * 4;
        const pullX = rawValues[valueIndex];
        const pullY = rawValues[valueIndex + 1];
        valueIndex += 2;
        data[index] = clamp((pullX / scale + 0.5) * 255, 0, 255);
        data[index + 1] = clamp((pullY / scale + 0.5) * 255, 0, 255);
        data[index + 2] = 0;
        data[index + 3] = 255;
      }
    }

    context.putImageData(image, 0, 0);
    return { href: canvas.toDataURL('image/png'), scale };
  }

  function createFilter(element, options) {
    const rect = element.getBoundingClientRect();
    if (rect.width < 20 || rect.height < 20) return;

    const host = ensureHost();
    const id = `sts-liquid-glass-${Math.random().toString(36).slice(2, 10)}`;
    const width = Math.round(rect.width);
    const height = Math.round(rect.height);
    const sampleWidth = Math.min(options.sampleWidth, width);
    const sampleHeight = Math.max(32, Math.round((sampleWidth / width) * height));
    const map = buildMap(sampleWidth, sampleHeight, options.radius, options.strength);

    const defs = document.createElementNS(svgNS, 'defs');
    const filter = document.createElementNS(svgNS, 'filter');
    filter.setAttribute('id', id);
    filter.setAttribute('filterUnits', 'userSpaceOnUse');
    filter.setAttribute('x', '0');
    filter.setAttribute('y', '0');
    filter.setAttribute('width', String(width));
    filter.setAttribute('height', String(height));
    filter.setAttribute('color-interpolation-filters', 'sRGB');

    const image = document.createElementNS(svgNS, 'feImage');
    image.setAttribute('href', map.href);
    image.setAttribute('x', '0');
    image.setAttribute('y', '0');
    image.setAttribute('width', String(width));
    image.setAttribute('height', String(height));
    image.setAttribute('preserveAspectRatio', 'none');
    image.setAttribute('result', 'stsLiquidMap');

    const displacement = document.createElementNS(svgNS, 'feDisplacementMap');
    displacement.setAttribute('in', 'SourceGraphic');
    displacement.setAttribute('in2', 'stsLiquidMap');
    displacement.setAttribute('scale', map.scale.toFixed(2));
    displacement.setAttribute('xChannelSelector', 'R');
    displacement.setAttribute('yChannelSelector', 'G');

    filter.appendChild(image);
    filter.appendChild(displacement);
    defs.appendChild(filter);
    host.appendChild(defs);

    element.style.setProperty('--sts-liquid-filter', `url("#${id}")`);
    element.setAttribute('data-sts-liquid-glass', 'ready');
    instances.set(element, { defs, width, height, options });
  }

  function hydrate() {
    targets.forEach((options) => {
      document.querySelectorAll(options.selector).forEach((element) => {
        const existing = instances.get(element);
        const rect = element.getBoundingClientRect();
        if (existing && Math.abs(existing.width - rect.width) < 8 && Math.abs(existing.height - rect.height) < 8) {
          return;
        }
        if (existing) existing.defs.remove();
        createFilter(element, options);
      });
    });
  }

  let resizeTimer;
  function requestHydrate() {
    clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(hydrate, 120);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate, { once: true });
  } else {
    hydrate();
  }

  window.addEventListener('resize', requestHydrate, { passive: true });
  document.addEventListener('shopify:section:load', requestHydrate);
})();
